import { ReactElement, useState } from 'react';
import { GetOverviewSummary } from '../types';
import { useTranslation } from 'react-i18next';
import { UnitsTooltipContent } from './styles';
import { BigNumberCard } from './components/Card';
import { useHistory } from 'react-router-dom';
import { handleFilterStateRoute } from '../helpers/filters';
import { useFilterContext } from '../contexts/FilterContext';
import { getUserProfile } from 'helpers/userProfile';
import { normalizeNumber } from '../helpers/numbers';

interface UnitsProps {
  data: GetOverviewSummary['UNITS'] | undefined;
  loading: boolean;
}

type UnitsTooltipProps = Pick<UnitsProps, 'data'>;

interface TooltipRowProps {
  label: string;
  value?: number;
  subtext?: string;
}

export function Units({
  data,
  loading,
}: Readonly<UnitsProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const { t } = useTranslation();
  const { selectedOptions, clients } = useFilterContext();

  function onClickUnitsDetails(): void {
    history.push('/analise/unidades',
      handleFilterStateRoute(clients, selectedOptions, profile));
  }

  return (
    <BigNumberCard
      id="unit-count"
      icon="Building3Icon"
      number={String(normalizeNumber(data?.TOTAL))}
      description={{
        text: t('unidadesSobGestao'),
        tooltip: <UnitsTooltip data={data} />,
      }}
      subDescription={{
        number: normalizeNumber(data?.OPERATIONAL),
        text: t('unidadesOperacao'),
      }}
      onClickDetails={onClickUnitsDetails}
      loading={loading}
      disabledTooltip={loading || !data}
      dataTestId="big-number-card-unit"
    />
  );
}

function UnitsTooltip({
  data,
}: Readonly<UnitsTooltipProps>): ReactElement {
  const { t } = useTranslation();

  return (
    <UnitsTooltipContent>
      <TooltipRow label={t('unidades')} />
      <TooltipRow label={t('monitoradas')} value={normalizeNumber(data?.MONITORED)} />
      <TooltipRow label={t('emOperacao')} value={normalizeNumber(data?.OPERATIONAL)} subtext={t('unidadesOperandoCorretamente')} />
      <TooltipRow label={t('foraDeOperacao')} value={normalizeNumber(data?.INACTIVE)} subtext={t('unidadesEmReformaOuEmDesinstalaçãoForcadaDosIoT')} />
      {data?.INSTALLATION !== undefined && (
        <TooltipRow label={t('unidadesEmInstalacao', { units: normalizeNumber(data.INSTALLATION) })} />
      )}
    </UnitsTooltipContent>
  );
}

function TooltipRow({
  label,
  value,
  subtext,
}: Readonly<TooltipRowProps>): ReactElement {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1, lineHeight: 1.2,
    }}
    >
      <span>
        {value === undefined ? label : `${label}: ${normalizeNumber(value)}`}
      </span>
      {subtext && (
        <span style={{ lineHeight: 1.2 }}>
          {' '}
          {subtext}
        </span>
      )}
    </div>
  );
}
